import { getCookieValue } from "@/utils/commonUtils";

import { MARKETING_YANDEX_ID } from "./constants";
import { GtmDataLayerObject } from "./types";
import { whenYandexMetrikaReady } from "./yandexMetrika";

// Последний userID, уже отправленный в параметры визита.
// Живёт в памяти модуля: сбрасывается при перезагрузке страницы, но переживает
// переходы внутри SPA — поэтому на каждый переход по сайту повторной отправки нет.
let lastSentVisitUserID: number | null = null;

export default abstract class MarketingUtils {
  static reachYandexMetricsGoal(goalId: string, userId: null | number, params?: object) {
    console.log("Goal reached:", goalId, { userID: userId, ...params });
    if (typeof window?.ym !== "undefined") {
      window.ym(
        MARKETING_YANDEX_ID,
        "reachGoal",
        goalId,
        {
          userID: userId,
          ym_uid: getCookieValue("_ym_uid") || "",
          ...params,
        },
      );
    }
  }

  /**
   * Отправляет userID в параметры ВИЗИТА (`ym('params')`), а не в параметры цели.
   *
   * Зачем: `reachGoal` передаёт userID только вместе с событием. Если за визит
   * не сработала ни одна цель (человек залогинился и просто читал статьи),
   * пользователь остаётся неопознанным в отчётах. `params` привязывает userID
   * ко всему визиту, независимо от целей.
   *
   * Для неавторизованных не отправляем никогда: Метрика запишет `null` строкой
   * 'null', и в отчёте будет выглядеть, будто опознаны почти все, хотя не опознан никто.
   *
   * Имя параметра — строго `userID`, так его ждёт аналитика.
   */
  static async sendYandexMetricsVisitUserID(userId: null | number) {
    if (typeof userId !== "number") return false;
    if (lastSentVisitUserID === userId) return false;

    const isMetrikaReady = await whenYandexMetrikaReady();
    if (!isMetrikaReady) return false;

    lastSentVisitUserID = userId;
    window.ym(MARKETING_YANDEX_ID, "params", { userID: userId });
    return true;
  }

  static reachCustomYandexMetricsGoal(goalId: string, userId: null | number, params?: object) {
    console.log("Goal reached:", goalId, ", params:", params);
    if (typeof window?.ym !== "undefined") {
      window.ym(
        MARKETING_YANDEX_ID,
        "reachGoal",
        goalId,
        {
          userID: userId,
          ...params,
        },
      );
    }
    return true;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  static pushToDataLayer(_objectToPush: GtmDataLayerObject | Record<string, any>) {
    // Google Tag Manager / GA removed from frontend — dataLayer pushes intentionally disabled.
  }

  static marketingMainPageReadMoreClickedHandler(userId: null | number) {
    MarketingUtils.reachYandexMetricsGoal("Main-Page-Read-More-Clicked", userId);
    return true;
  }

  static marketingCallStartedHandler(userId: null | number) {
    MarketingUtils.reachYandexMetricsGoal("Call-Started", userId);
    return true;
  }

  static marketingPhoneCallStartedHandler(userId: null | number) {
    MarketingUtils.reachYandexMetricsGoal("phone_call", userId);
    return true;
  }

  static marketingSessionStartedHandler(userId: null | number) {
    MarketingUtils.reachYandexMetricsGoal("Session-Started", userId);
    return true;
  }

  static marketingAudioGreetingPlayHandler(userId: null | number) {
    MarketingUtils.reachYandexMetricsGoal("Audio-Greeting-Play", userId);
    return true;
  }

  static marketingMainPage1(userId: null | number) { MarketingUtils.reachYandexMetricsGoal("see_experts1", userId); return true; }

  static marketingMainPage2(userId: null | number) { MarketingUtils.reachYandexMetricsGoal("see_experts2", userId); return true; }

  static marketingMainPage3(userId: null | number) { MarketingUtils.reachYandexMetricsGoal("see_experts3", userId); return true; }

  static marketingMainPage4(userId: null | number) { MarketingUtils.reachYandexMetricsGoal("see_experts4", userId); return true; }

  static marketingMainPage5(userId: null | number) { MarketingUtils.reachYandexMetricsGoal("Ask_Question", userId); return true; }

  static marketingMainPage6(userId: null | number) { MarketingUtils.reachYandexMetricsGoal("get_free", userId); return true; }

  static marketingContinueConsultation(userId: null | number) {
    MarketingUtils.reachYandexMetricsGoal("continue_consultation", userId);
    return true;
  }

  static marketingViewCatalog(userId: null | number) {
    MarketingUtils.reachYandexMetricsGoal("view_catalog", userId);
    return true;
  }

  static marketingQuizFinalGoalHandler(goalName: string, userId: null | number) {
    MarketingUtils.reachYandexMetricsGoal(goalName, userId);
    MarketingUtils.pushToDataLayer({ event: goalName });
    return true;
  }

  static marketingQuizStepHandler(goalName: string, userId: null | number, params?: object) {
    MarketingUtils.reachYandexMetricsGoal(goalName, userId, {
      event: goalName,
      ...params,
    });
    MarketingUtils.pushToDataLayer({
      event: goalName,
      ...params,
    });
    return true;
  }

  static customMarketingHandler(metricsId: string, userId: null | number) {
    if (!metricsId) { return false; }
    MarketingUtils.reachYandexMetricsGoal(metricsId, userId);
    MarketingUtils.pushToDataLayer({ event: metricsId });
    return true;
  }

  static marketingTrackFlocktoryExchange(clientEmail: string, userId: null | number) {
    MarketingUtils.reachYandexMetricsGoal("flocktory_exchange_gift", userId, {
      event: "flocktory_exchange_gift",
      user_email: clientEmail,
    });
    MarketingUtils.pushToDataLayer({ event: "flocktory_exchange_gift", user_email: clientEmail });
    return true;
  }

  static marketingPromocodeAppliedHandler(userId: null | number, promocode?: string) {
    if (!promocode) { return false; }
    MarketingUtils.reachYandexMetricsGoal("promocode", userId, { promocode });
    MarketingUtils.pushToDataLayer({ event: "promocode", promocode });
    return true;
  }

  static marketingOnboardingAnswers(userId: null | number, step?: string, answer?: string) {
    MarketingUtils.reachYandexMetricsGoal("onboarding", userId, {
      event: "onboarding_trigger_modal",
      step: step || "",
      answer: answer || "",
    });
    MarketingUtils.pushToDataLayer({
      event: "onboarding_trigger_modal",
      step: step || "",
      answer: answer || "",
    });
    return true;
  }

  static marketingStepsForFixPaymentFromLandingPage({
    buttonText = "",
    bannerName = "",
    fixPaymentAmount = "",
    userId,
    productId = "",
  }: {
    buttonText?: string;
    bannerName?: string;
    fixPaymentAmount?: string;
    userId: null | number;
    productId?: string;
  }) {
    MarketingUtils.reachYandexMetricsGoal("fixed_service", userId, {
      buttonText,
      bannerName,
      fixPaymentAmount,
      productId,
      event: "fix_payment_from_landing_page",
      datalayer_user_id: userId,
      landing_button_name: buttonText,
      fix_payment_banner: bannerName,
      datalayer_amount: fixPaymentAmount,
      product_id: productId,
    });
    MarketingUtils.pushToDataLayer({
      event: "fix_payment_from_landing_page",
      datalayer_user_id: userId,
      landing_button_name: buttonText,
      fix_payment_banner: bannerName,
      datalayer_amount: fixPaymentAmount,
      product_id: productId,
    });
    return true;
  }

  static debugSessionPayment({ goal, sessionId, userId }: { goal: string; sessionId: string, userId: null | number }) {
    MarketingUtils.reachYandexMetricsGoal(goal, userId, { event: goal, sessionId });
    MarketingUtils.pushToDataLayer({ event: goal, sessionId });
  }
}
